import React, { useEffect, useState } from 'react'

import update from 'react-addons-update'
import { useNavigate } from 'react-router'
import { useToasts } from 'react-toast-notifications'
import styled from 'styled-components'

import { GlobalModalTypeEnum, ModalsPlugin } from '@api/local/ModalsPlugin'
import { Button, Heading, Paragraph, ResponsiveImage, Spacer } from '@atoms/index'
import { useConfig } from '@client/contexts/ConfigProvider'
import { ProductCardContainer, ProductCardContentContainer, ProductCardImageContainer, ServingSizeEnum } from '@components/Theme'
import { useEvents } from '@contexts/GTMProvider'
import { FrozenMealDishListFragment, FrozenMealListFragment, useAddItemToCartMutation, useChangeCartItemQuantityMutation, useRemoveItemFromCartMutation, useUserDetailsQuery } from '@hooks/api'
import { ItemInterfaceNew } from '@lib/GTM'
import { Utilities } from '@lib/Utilities'
import { convertEnumToNumFRZN, FieldData, Form, RadioButton, RadioButtonProps, useForm } from '@molecules/index'
import { FrozenPortionSizeEnum, ProductAvailabilityEnum, ProductStockStatusEnum } from '@uctypes/api/globalTypes'

import { FloatingUpSell } from './FloatingUpSell'

const Container = styled.div`
  ${ProductCardContainer}

  .flex-form {
    display: flex;
    flex-direction: column;
    flex: 1;
  }
`

const ImageContainer = styled.div`
  ${ProductCardImageContainer}
`

const LinkContainer = styled.a`
  cursor: pointer;
  text-decoration: none;
`

const ContentContainer = styled.div`
  ${ProductCardContentContainer}
  .serving-size {
    display: flex;
    flex: 0;
    margin: 0;
  }
`
const PriceContainer = styled.div`
`
export interface FrozenMyShopCardProps {
  craftMeal: FrozenMealDishListFragment
  className?: string
  loading?: boolean
}

interface FrozenMyShopCardState {
  addToCartInterrupted: boolean
  displayingPriceRange: boolean
  price: number | string
  loading: boolean
  servingSize: FrozenPortionSizeEnum

}

const DEFAULT_STATE: FrozenMyShopCardState = {
  addToCartInterrupted: false,
  displayingPriceRange: false,
  price: 0,
  loading: false,
  servingSize: FrozenPortionSizeEnum.SERVES_ONE,
}

export function FrozenMyShopCard({ craftMeal, className, loading = false }: FrozenMyShopCardProps): JSX.Element {

  const config = useConfig()
  const [form] = useForm()
  const [state, setState] = useState<FrozenMyShopCardState>({ ...DEFAULT_STATE })
  const { data: userDetailsData } = useUserDetailsQuery({ ssr: config.fetchSSRQuery() })
  const [addItemToCart] = useAddItemToCartMutation()
  const [removeItemFromCart] = useRemoveItemFromCartMutation()
  const { addToast } = useToasts()
  const pdpLink = `/frozen/bought-before/${craftMeal?.slug}`
  const navigate = useNavigate()
  const servingSizeNumber = convertEnumToNumFRZN(state.servingSize)

  const isAvailable = (): boolean => {
    let available = true
    for (let i = 0; i < craftMeal?.products?.length; i++) {
      if (craftMeal.products[i].availability === ProductAvailabilityEnum.UNAVAILABLE_GEOGRAPHICALLY) {
        available = false
        break
      }
    }
    return available
  }
  const events = useEvents()

  const [changeCartItemQuantity] = useChangeCartItemQuantityMutation()
  const _handleOnAdd = async (): Promise<void> => {

    const noDefaultAddress = !userDetailsData?.currentUser?.hasDefaultAddress

    setState((prevState) => ({ ...prevState, addToCartInterrupted: noDefaultAddress || !isAvailable() }))

    if (noDefaultAddress) {
      ModalsPlugin.shared().toggleGlobalModal(true, GlobalModalTypeEnum.ADD_LOCATION)
    } else if (isAvailable()) {

      setState((prevState) => ({ ...prevState, loading: true }))

      const servingSize = form.getFieldValue('servingSizeOptionsRadio')
      const product = craftMeal?.products?.find(product => product.id === servingSize)
      try {
        await addItemToCart({
          variables: { productId: servingSize, quantity: 1 },
        })

        const logData = {
          itemName: craftMeal?.name,
          itemId: product?.id,
          itemGroupId: craftMeal?.id,
          price: product?.price,
          itemBrand: 'UCOOK',
          itemCategory: craftMeal?.frozenCategories?.map((cat) => cat.id)?.join(', '),
          itemVariant: craftMeal?.frozenCategories?.map((cat) => cat.title)?.join(', '),
          itemListName: 'Craft Meals',
          quantity: 1,
          itemImage: craftMeal?.coverImage?.location,
          itemStockCount: product?.stockCount > 1 ? 1 : 0,
          itemChef: 'UCOOK',
          isMealkit: 'no',
          itemServingSize: servingSizeNumber,
        }

        const snakedData = Utilities.toSnakeCase(logData) as unknown as ItemInterfaceNew
        events.hasAddedToCart(snakedData)

        addToast('Item successfully added to cart', {
          appearance: 'success',
          autoDismiss: true,
        })
      } catch (e) {
        addToast(e.message, {
          appearance: 'error',
          autoDismiss: true,
        })
      }
      setState((prevState) => ({ ...prevState, loading: false }))
    } else {
      ModalsPlugin.shared().toggleGlobalModal(true, GlobalModalTypeEnum.DELIVERY_UNAVAILABLE)
    }
  }

  const getCraftMealPriceRange = (craftMeal: FrozenMealDishListFragment): string => {

    const getPortionPrice = (portionSize: FrozenPortionSizeEnum): number => {
      return craftMeal?.products?.find(product => product.frozenPortionSize === portionSize)?.price || 0
    }

    const lower = getPortionPrice(FrozenPortionSizeEnum.SERVES_ONE)
    const upper = getPortionPrice(FrozenPortionSizeEnum.SERVES_FOUR)
    return `${lower} - R${upper}`
  }

  const getServingSizeTitle = (product: FrozenMealListFragment): string => ServingSizeEnum[product.frozenPortionSize].toLowerCase() as string

  const getServingSizeOptions = () => {
    const options = craftMeal?.products?.map((product) => ({
      title: getServingSizeTitle(product),
      disabled: product.stockStatus === ProductStockStatusEnum.OUT_OF_STOCK,
      value: product.id,
      quantity: product.quantityInCart,
    })) || []
    return options.sort((a, b) => a.title > b.title ? 1 : -1)
  }

  const getAvailableServing = (): string => {
    const servingSizeOptions = getServingSizeOptions()
    const availableProduct = servingSizeOptions?.find(option => !option.disabled)
    return availableProduct?.value
  }

  const servingSizeOptions = () => {
    const options = getServingSizeOptions()
    return options.map(option => {
      return {
        className: 'size-option',
        value: option.value,
        disabled: option.disabled,
        element: <Paragraph bold variant='p3'>{option.title}</Paragraph>,
        count: option.quantity,
      }
    })
  }

  const servingSizeOptionsRadioProps: RadioButtonProps = {
    className: 'serving-size',
    outline: false,
    name: 'servingSizeOptionsRadio',
    options: [...servingSizeOptions()],
    error: '',
    rules: [{ required: true, message: 'Please select a reason' }],
  }

  const _setPrice = (value: string) => {
    const selectedMeal = craftMeal?.products?.find(product => product.id === value)
    let price: number | string = selectedMeal?.price || 0
    setState((prevState) => update(prevState, { servingSize: { $set: selectedMeal?.frozenPortionSize } }))

    const shouldDisplayPriceRange = price === 0 || !isAvailable() || selectedMeal?.stockCount === 0

    if (shouldDisplayPriceRange) {
      price = getCraftMealPriceRange(craftMeal)
    }

    setState((prevState) => update(prevState, {
      price: { $set: price },
      displayingPriceRange: { $set: shouldDisplayPriceRange },
    }))
  }

  const _handleChange = (changedFields: FieldData[]) => {
    changedFields.forEach((field) => {
      (field.name as string[]).forEach((name) => {
        if (name === 'servingSizeOptionsRadio') {
          _setPrice(field.value)
        }
      })
    })
  }

  const _setCraftMealServingSize = (value: string): void => {
    if (config.isBrowser()) {
      window.sessionStorage.setItem('craftMealServingSize', value)
    }
  }

  const _handlePdpNavigation = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    _setCraftMealServingSize(form.getFieldValue('servingSizeOptionsRadio'))
    navigate(pdpLink)
  }
  const getSpecificServingSizeAmount = (): number => {
    const servingSize = form.getFieldValue('servingSizeOptionsRadio')
    return craftMeal?.products?.find(product => product.id === servingSize)?.quantityInCart || 0
  }
  const getAllSizesAmount = (): number => {
    let sum = 0
    craftMeal.products.forEach((prod) => (sum += prod.quantityInCart))
    return sum
  }

  const _handleOnRemove = async (): Promise<void> => {

    const product = craftMeal?.products?.find((product) => { return product.frozenPortionSize === state.servingSize })
    setState((prevState) => update(prevState, { loading: { $set: true } }))

    try {

      if (product.quantityInCart === 1) {
        await removeItemFromCart({
          variables: {
            productId: product.id,
          },
        })

      } else {
        await changeCartItemQuantity({
          variables: {
            productId: product.id,
            quantity: product.quantityInCart - 1,
          },
        })
      }

      const logData = {
        itemName: craftMeal?.name,
        itemId: product?.id,
        price: product?.price,
        itemBrand: 'UCOOK',
        itemCategory: craftMeal?.frozenCategories?.map((cat) => cat.id)?.join(', '),
        itemVariant: craftMeal?.frozenCategories?.map((cat) => cat.title)?.join(', '),
        itemListName: 'Craft Meals',
        quantity: 1,
        itemImage: craftMeal?.coverImage?.location,
        itemStockCount: product?.stockCount > 1 ? 1 : 0,
        itemChef: 'UCOOK',
        isMealkit: 'no',
        itemServingSize: servingSizeNumber,
      }

      const snakedData = Utilities.toSnakeCase(logData) as unknown as ItemInterfaceNew
      events.hasRemovedFromCart(snakedData)

      addToast('Item successfully removed from cart', {
        appearance: 'success',
        autoDismiss: true,
      })
    } catch (e) {
      addToast(e.message, {
        appearance: 'error',
        autoDismiss: true,
      })
    }
    setState((prevState) => update(prevState, { loading: { $set: false } }))

  }
  const _handleOnCenterClick = async (): Promise<void> => {
    if (product.quantityInCart === 0) {
      _handleOnAdd()
    }
  }
  const _handleOnLeftIconClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLDivElement, MouseEvent>) => {
    e.stopPropagation()
    _handleOnRemove()
  }
  const _handleOnRightIconClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLDivElement, MouseEvent>) => {
    e.stopPropagation()
    _handleOnAdd()
  }

  useEffect(() => {
    if (state.addToCartInterrupted) {
      _handleOnAdd()
    }
  }, [userDetailsData?.currentUser?.addresses, isAvailable()])

  useEffect(() => {
    const availableServing = getAvailableServing()
    form.setFieldsValue({
      servingSizeOptionsRadio: availableServing,
    })
    _setPrice(availableServing)
  }, [])

  const buttonText = isAvailable() && !state.displayingPriceRange ? 'ADD TO CART' : 'OUT OF STOCK'
  const categoryTitle = craftMeal?.frozenCategories[0]?.title
  const displayAmount = getAllSizesAmount()
  const amountServingSizeSpecific = getSpecificServingSizeAmount() || 0
  const product = craftMeal?.products?.find((product) => { return product.frozenPortionSize === state?.servingSize })
  const category = Utilities.getCategoryFromMeal(craftMeal)
  // Hierarchical text resolution: item-level takes precedence over category-level
  const upSellTexts = Utilities.getHierarchicalText(craftMeal?.upSellText, category?.upSellText)

  return (
    <Container className={className}>
      <For each='upSellText' of={upSellTexts}>
        <FloatingUpSell text={upSellText} />
      </For>
      <Form className='flex-form' form={form} onFieldsChange={_handleChange}>
        <LinkContainer onClick={_handlePdpNavigation}>
          <ImageContainer>
            <ResponsiveImage image={craftMeal.coverImage} lazy={false} />
          </ImageContainer>
        </LinkContainer>
        <ContentContainer className='fluid-grow'>
          <LinkContainer onClick={_handlePdpNavigation}>
            <Heading variant='h6'> {craftMeal.name} </Heading>
          </LinkContainer>
          <Spacer universal='4px' />
          <Paragraph variant='p2'>{craftMeal.subTitle} </Paragraph>
          <Spacer universal='8px' className='fluid-grow' />
          <If condition={categoryTitle === 'Nourish' || categoryTitle === 'Artisan' || categoryTitle === 'Classics' || categoryTitle === 'Sides'}>
            <RadioButton {...servingSizeOptionsRadioProps} />
            <Spacer universal='12px' />
          </If>
          <PriceContainer>
            <Heading variant='h5'> {`R${state.price}`} </Heading>
          </PriceContainer>
          <Spacer universal='12px' />

          <Button
            className='add-button'
            title={buttonText}
            color='black'
            fullWidth
            loading={loading || state.loading}
            disabled={loading || state.displayingPriceRange}
            amount={displayAmount}
            amountServingSizeSpecific={amountServingSizeSpecific}
            onClick={_handleOnCenterClick}
            onLeftIconClick={_handleOnLeftIconClick}
            onRightIconClick={_handleOnRightIconClick} />
        </ContentContainer>
      </Form>
    </Container>
  )
}
